/**
 * AddNodeModal — the "Add Node" picker.
 *
 * Extracted from <ChatSidebarDock>, which in turn carried a copy of the
 * legacy <CanvasToolbox> implementation. <CanvasBottomPalette> needed the
 * same picker, and a third copy of ~180 lines of allowlist + placement
 * logic was not worth it, so the one in ChatSidebarDock became this
 * component and both callers now render it.
 *
 * Owns everything the picker needs: the node allowlist, search, category
 * grouping, viewport-centre placement with collision nudging, and the
 * file-picker-first flow for upload node types. Callers supply only
 * open/close plus the two canvas callbacks.
 *
 * Behaviour is deliberately unchanged from the ChatSidebarDock version —
 * same test ids, same i18n keys under `canvasToolbox.*`.
 */

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type RefObject,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useReactFlow } from 'reactflow'
import { Search } from 'lucide-react'
import Modal from '../ui/Modal'
import { nodeSchemas, createNode, type RawNodeDataRecord, type NodeSchema } from './nodeRegistry'
import { NODE_TYPE_ICON } from './nodes/shared/nodeTypeIcons'
import { cn } from '../../lib/utils'
import type { ReactFlowCanvasEdgeData, ReactFlowCanvasNodeData } from '../../hooks/useYCanvas'
import { usePaletteAnalytics, type PaletteInteraction } from '../../analytics/paletteAnalytics'
import { noteNodeActions } from '../../analytics/nodeActionAnalytics'

/** The 12 user-facing node types, mapped to their display category. */
const ALLOWED_TYPES: Record<string, string> = {
  characterDesigner: 'Agents',
  locationDesigner: 'Agents',
  objectDesigner: 'Agents',
  imageGenerator: 'Agents',
  videoGenerator: 'Agents',
  soundExpert: 'Agents',
  videoEditor: 'Agents',
  scriptWriter: 'Agents',
  image: 'Media',
  video: 'Media',
  audio: 'Media',
  nodeGroup: 'Layout',
}

const CATEGORY_I18N_KEYS: Record<string, string> = {
  Agents: 'canvasToolbox.categoryAgents',
  Media: 'canvasToolbox.categoryMedia',
  Layout: 'canvasToolbox.categoryLayout',
}

/** Types that open the OS file picker first rather than creating an empty node. */
const UPLOAD_NODE_TYPES: Record<
  string,
  { mediaType: 'image' | 'video' | 'audio'; accept: string }
> = {
  image: { mediaType: 'image', accept: 'image/*' },
  video: { mediaType: 'video', accept: 'video/*' },
  audio: { mediaType: 'audio', accept: 'audio/*' },
}

export interface AddNodeModalProps {
  isOpen: boolean
  onClose: () => void
  onAddNode: (node: ReturnType<typeof createNode>, explicitData?: RawNodeDataRecord) => void
  onUploadFile?: (file: File, type: 'image' | 'video' | 'audio') => void
  /**
   * Element used to locate the ReactFlow container for viewport-centre
   * math. Falls back to `window.innerWidth` when absent or detached.
   */
  anchorRef?: RefObject<HTMLElement | null>
}

export const AddNodeModal = memo(function AddNodeModal({
  isOpen,
  onClose,
  onAddNode,
  onUploadFile,
  anchorRef,
}: AddNodeModalProps) {
  const { t } = useTranslation('canvas')
  const [search, setSearch] = useState('')
  const { getViewport, getNodes } = useReactFlow<
    ReactFlowCanvasNodeData,
    ReactFlowCanvasEdgeData
  >()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const pendingMediaTypeRef = useRef<'image' | 'video' | 'audio'>('image')
  // RFC-1577 attribution. Upload types close this picker BEFORE the OS file
  // dialog resolves, so the palette interaction is captured at pick time and
  // the file outcome is attributed to it after the close.
  const analytics = usePaletteAnalytics()
  const pendingUploadInteractionRef = useRef<PaletteInteraction | null>(null)
  const searchLoggedRef = useRef(false)

  // The picker stays mounted, so clear the query each time it opens —
  // ChatSidebarDock did this in its open handler before the extraction.
  useEffect(() => {
    if (isOpen) {
      setSearch('')
      searchLoggedRef.current = false
    }
  }, [isOpen])

  // The OS dialog's Cancel fires a native `cancel` event (no React prop).
  useEffect(() => {
    const input = fileInputRef.current
    if (!input) return
    const onCancel = () => {
      const interaction = pendingUploadInteractionRef.current
      pendingUploadInteractionRef.current = null
      analytics?.step('add_node', 'file_cancelled', { option: pendingMediaTypeRef.current }, interaction)
    }
    input.addEventListener('cancel', onCancel)
    return () => input.removeEventListener('cancel', onCancel)
  }, [analytics])

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value)
      // Once per open, and never the query itself (free text).
      if (value.trim() && !searchLoggedRef.current) {
        searchLoggedRef.current = true
        analytics?.step('add_node', 'search')
      }
    },
    [analytics],
  )

  const groupedNodes = useMemo(() => {
    const groups: Record<string, Array<{ type: string; schema: NodeSchema }>> = {}
    for (const [type, category] of Object.entries(ALLOWED_TYPES)) {
      const schema = nodeSchemas[type]
      if (!schema) continue
      if (!groups[category]) groups[category] = []
      groups[category].push({ type, schema })
    }
    return groups
  }, [])

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groupedNodes
    const q = search.toLowerCase()
    const out: typeof groupedNodes = {}
    for (const [cat, nodes] of Object.entries(groupedNodes)) {
      const hits = nodes.filter(
        ({ type, schema }) =>
          schema.label.toLowerCase().includes(q) ||
          (schema.description && schema.description.toLowerCase().includes(q)) ||
          type.toLowerCase().includes(q),
      )
      if (hits.length) out[cat] = hits
    }
    return out
  }, [groupedNodes, search])

  const handlePick = useCallback(
    (type: string) => {
      analytics?.step('add_node', 'pick', { option: type })
      const uploadInfo = UPLOAD_NODE_TYPES[type]
      if (uploadInfo && onUploadFile && fileInputRef.current) {
        pendingUploadInteractionRef.current = analytics?.active('add_node') ?? null
        pendingMediaTypeRef.current = uploadInfo.mediaType
        fileInputRef.current.accept = uploadInfo.accept
        fileInputRef.current.click()
        onClose()
        return
      }

      const vp = getViewport()
      const rfContainer = anchorRef?.current?.closest('.react-flow')
      const cw = rfContainer ? rfContainer.getBoundingClientRect().width : window.innerWidth
      const cx = (-vp.x + cw / 2) / vp.zoom
      const cy = (-vp.y + window.innerHeight / 2) / vp.zoom

      const OFFSET_STEP = 40
      let offsetX = 0
      let offsetY = 0
      const existingPositions = getNodes().map((n) => n.position)
      for (let i = 0; i < 10; i++) {
        const testX = cx + offsetX
        const testY = cy + offsetY
        const tooClose = existingPositions.some(
          (p) => Math.abs(p.x - testX) < 50 && Math.abs(p.y - testY) < 50,
        )
        if (!tooClose) break
        offsetX += OFFSET_STEP
        offsetY += OFFSET_STEP
      }
      const position = { x: cx + offsetX, y: cy + offsetY }

      // The parent pans to the new node once ReactFlow has measured it.
      const newNode = createNode(type, position)
      noteNodeActions([newNode.id], 'add', 'add_menu')
      onAddNode(newNode, {})

      onClose()
    },
    [analytics, anchorRef, getViewport, getNodes, onAddNode, onUploadFile, onClose],
  )

  const handleFileSelected = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      const interaction = pendingUploadInteractionRef.current
      pendingUploadInteractionRef.current = null
      analytics?.step(
        'add_node',
        file ? 'file_selected' : 'file_cancelled',
        { option: pendingMediaTypeRef.current, item_count: file ? 1 : undefined },
        interaction,
      )
      if (file) onUploadFile?.(file, pendingMediaTypeRef.current)
      e.target.value = ''
    },
    [analytics, onUploadFile],
  )

  const hasResults = Object.keys(filteredGroups).length > 0

  return (
    <>
      {/* Hidden file input for upload-type nodes (file-picker-first) */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelected}
        className="hidden"
        data-testid="toolbox-upload-file-input"
      />

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('canvasToolbox.addNodeTitle')}
        size="md"
      >
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={t('canvasToolbox.searchNodes')}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className={cn(
                'w-full rounded-lg border border-border bg-muted py-2 pl-9 pr-3 text-sm',
                'text-foreground placeholder-muted-foreground',
                'focus:border-chrome/50 focus:outline-none transition-colors',
              )}
              autoFocus
              data-testid="toolbox-search"
            />
          </div>
          <div className="-mx-1 max-h-72 overflow-y-auto px-1">
            {!hasResults && (
              <div
                className="py-8 text-center text-xs font-medium text-muted-foreground"
                data-testid="toolbox-empty-state"
              >
                {search ? t('canvasToolbox.noMatches') : t('canvasToolbox.noNodesAvailable')}
              </div>
            )}
            {Object.entries(filteredGroups).map(([category, categoryNodes]) => (
              <div key={category} className="mb-2" data-testid={`toolbox-category-${category}`}>
                <div className="px-1 pt-2 pb-1.5">
                  <span className="text-xxs font-medium uppercase tracking-wider text-muted-foreground">
                    {CATEGORY_I18N_KEYS[category] ? t(CATEGORY_I18N_KEYS[category]) : category}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {categoryNodes.map(({ type, schema }) => (
                    <button
                      key={type}
                      onClick={() => handlePick(type)}
                      className={cn(
                        'flex items-center gap-2 rounded-lg px-2.5 py-2 text-left',
                        'hover:bg-muted cursor-pointer transition-colors group',
                      )}
                      title={schema.description || `Add ${schema.label}`}
                      data-testid={`toolbox-node-${type}`}
                    >
                      <span
                        className={cn(
                          'h-4 w-4 shrink-0 text-muted-foreground opacity-70 transition-opacity',
                          'group-hover:text-foreground group-hover:opacity-100',
                          '[&>svg]:h-4 [&>svg]:w-4',
                        )}
                        aria-hidden="true"
                        data-testid={`toolbox-node-icon-${type}`}
                      >
                        {NODE_TYPE_ICON[type] ?? NODE_TYPE_ICON.nodeGroup}
                      </span>
                      <span className="truncate text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                        {schema.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </>
  )
})
